import React, { useEffect, useMemo, useRef, useState } from 'react'
import classnames from 'classnames'

import { Button, BUTTON_THEME, Icon } from '@st-operations/uikit/src/v2/index.js'

import RadioGroup from '_components/radio-input-group'
import { moveAudiencesToPdp } from '_services/audience'

import AudienceLink from '../../bulk-edit-audiences/components/audience-link'
import ProgressBar from '../../bulk-edit-audiences/components/progress-bar'
import { COLLECTION } from '../collection'
import {
  BATCH_SIZE,
  MAX_CONSECUTIVE_BATCH_FAILURES,
  RESULT_STATUS,
  SKIP_REASONS,
  chunk,
  downloadMoveReport,
  formatSkipReason,
  getRetryableIds,
  mergeBatchResult,
} from '../lib/move'

const STATUS_LABELS = {
  [RESULT_STATUS.PENDING]: 'Pending',
  [RESULT_STATUS.MOVING]: 'Moving…',
  [RESULT_STATUS.MOVED]: 'Moved',
  [RESULT_STATUS.SKIPPED]: 'Skipped',
  [RESULT_STATUS.FAILED]: 'Failed',
}

const FILTERS = {
  ALL: 'all',
  FAILURES: 'failures',
  WARNINGS: 'warnings',
}

const StatusBadge = ({ status }) => (
  <span className={classnames('move-to-pdp-run__status', `move-to-pdp-run__status--${status}`)}>
    <Choose>
      <When condition={status === RESULT_STATUS.MOVED}>
        <Icon icon="checkmark" type="fill" width="14px" height="14px" color="#13682B" />
      </When>
      <When condition={status === RESULT_STATUS.FAILED}>
        <Icon icon="error" type="fill" width="14px" height="14px" color="#C0392B" />
      </When>
      <When condition={status === RESULT_STATUS.MOVING}>
        <span className="bulk-edit-run__spinner" />
      </When>
      <Otherwise>
        <span className="bulk-edit-run__dot" />
      </Otherwise>
    </Choose>
    {STATUS_LABELS[status] || status}
  </span>
)

const pendingResults = (ids) =>
  Object.fromEntries(ids.map((id) => [id, { status: RESULT_STATUS.PENDING, warnings: [] }]))

const MoveStage = ({ audiences, organization, env, onNewMove }) => {
  const [results, setResults] = useState(() => pendingResults(audiences.map(({ _id }) => _id)))
  const [running, setRunning] = useState(false)
  const [filter, setFilter] = useState(FILTERS.ALL)

  // refs keep the loop in sync without waiting for re-renders
  const resultsRef = useRef(results)
  const stopRef = useRef(false)
  const startedAtRef = useRef(new Date().toISOString())

  const updateResults = (next) => {
    resultsRef.current = next
    setResults(next)
  }

  const markBatch = (ids, result) =>
    updateResults({
      ...resultsRef.current,
      ...Object.fromEntries(ids.map((id) => [id, { warnings: [], ...result }])),
    })

  const downloadReport = () =>
    downloadMoveReport({
      audiences,
      results: resultsRef.current,
      organization,
      env: env.value,
      startedAt: startedAtRef.current,
    })

  const run = async (ids) => {
    setRunning(true)
    stopRef.current = false
    updateResults({ ...resultsRef.current, ...pendingResults(ids) })

    let consecutiveFailures = 0
    let abortedByErrors = false

    for (const batch of chunk(ids, BATCH_SIZE)) {
      if (stopRef.current || abortedByErrors) {
        markBatch(batch, { status: RESULT_STATUS.SKIPPED, reason: SKIP_REASONS.STOPPED })
        continue
      }

      markBatch(batch, { status: RESULT_STATUS.MOVING })

      let response
      try {
        response = await moveAudiencesToPdp(batch, organization._id, env.value)
      } catch (error) {
        // a rejected request (network, auth, validation) fails the whole batch
        response = { error: error?.message || 'Request failed' }
      }
      updateResults(mergeBatchResult(resultsRef.current, response, batch))

      if (response?.error) {
        consecutiveFailures += 1
        if (consecutiveFailures >= MAX_CONSECUTIVE_BATCH_FAILURES) {
          abortedByErrors = true
          toast.error(
            `Stopped: ${consecutiveFailures} batches in a row failed ("${response.error}"). Remaining audiences were skipped.`
          )
        }
      } else {
        consecutiveFailures = 0
      }
    }

    setRunning(false)
    downloadReport()
  }

  useEffect(() => {
    run(audiences.map(({ _id }) => _id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // discourage closing the tab mid-process
  useEffect(() => {
    if (!running) return
    const warn = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [running])

  const counts = useMemo(
    () =>
      Object.values(results).reduce(
        (acc, { status }) => ({ ...acc, [status]: (acc[status] || 0) + 1 }),
        {}
      ),
    [results]
  )

  const processedCount =
    (counts[RESULT_STATUS.MOVED] || 0) +
    (counts[RESULT_STATUS.SKIPPED] || 0) +
    (counts[RESULT_STATUS.FAILED] || 0)

  const retryableIds = getRetryableIds(results)

  const failureItems = audiences.filter(({ _id }) =>
    [RESULT_STATUS.FAILED, RESULT_STATUS.SKIPPED].includes(results[_id]?.status)
  )
  const warningItems = audiences.filter(({ _id }) => results[_id]?.warnings?.length > 0)

  const visibleItems = {
    [FILTERS.ALL]: audiences,
    [FILTERS.FAILURES]: failureItems,
    [FILTERS.WARNINGS]: warningItems,
  }[filter]

  const filterLabels = {
    [FILTERS.ALL]: `All (${audiences.length})`,
    [FILTERS.FAILURES]: `Failures only (${failureItems.length})`,
    [FILTERS.WARNINGS]: `With warnings (${warningItems.length})`,
  }

  const filterOptions = Object.values(FILTERS).map((value) => ({
    label: filterLabels[value],
    checked: filter === value,
  }))

  // the local Radio reports the selected option by its label
  const handleFilterChange = (event) => {
    const selected = Object.values(FILTERS).find(
      (value) => filterLabels[value] === event.target.value
    )
    if (selected) setFilter(selected)
  }

  return (
    <div className="bulk-edit-run">
      <section className="bulk-edit__card">
        <header className="bulk-edit__card-header">
          <h3>
            4. Moving to {organization.name} ({env.label})
          </h3>
        </header>

        <If condition={running}>
          <div className="bulk-edit__warning">
            Moving {BATCH_SIZE} audiences at a time from your browser — keep this tab open until it
            finishes.
          </div>
        </If>

        <ProgressBar loaded={processedCount} total={audiences.length} label="Processed" />

        <p className="bulk-edit__summary">
          {counts[RESULT_STATUS.MOVED] || 0} moved · {counts[RESULT_STATUS.SKIPPED] || 0} skipped ·{' '}
          {counts[RESULT_STATUS.FAILED] || 0} failed
        </p>

        <div className="bulk-edit-run__filters">
          <span className="bulk-edit-run__filters-label">Show:</span>
          <RadioGroup
            groupName="move-to-pdp-run-filter"
            className="bulk-edit-run__filters-radios"
            options={filterOptions}
            onChange={handleFilterChange}
          />
        </div>

        <ul className="bulk-edit-run__list">
          <For each="audience" of={visibleItems}>
            <li key={audience._id} className="bulk-edit-run__item">
              <StatusBadge status={results[audience._id]?.status} />
              <AudienceLink
                className="bulk-edit-run__name"
                href={COLLECTION.audienceUrl(audience._id)}
                name={audience.name}
                id={audience._id}
              />
              <If condition={results[audience._id]?.error}>
                <div className="bulk-edit-run__error">{results[audience._id].error}</div>
              </If>
              <If condition={results[audience._id]?.reason}>
                <div className="move-to-pdp-run__detail">
                  {formatSkipReason(results[audience._id].reason)}
                </div>
              </If>
              <If condition={results[audience._id]?.warnings?.length > 0}>
                <details className="move-to-pdp-run__warnings">
                  <summary>{results[audience._id].warnings.length} warning(s)</summary>
                  <ul>
                    <For each="warning" index="index" of={results[audience._id].warnings}>
                      <li key={index}>{warning}</li>
                    </For>
                  </ul>
                </details>
              </If>
            </li>
          </For>
        </ul>
      </section>

      <div className="bulk-edit__stage-actions">
        <Choose>
          <When condition={running}>
            <Button theme={BUTTON_THEME.SECONDARY} onClick={() => (stopRef.current = true)}>
              Stop (skips the remaining audiences)
            </Button>
          </When>
          <Otherwise>
            <Button theme={BUTTON_THEME.SECONDARY} onClick={onNewMove}>
              New Move
            </Button>
            <If condition={retryableIds.length > 0}>
              <Button theme={BUTTON_THEME.SECONDARY} onClick={() => run(retryableIds)}>
                Retry Failed ({retryableIds.length})
              </Button>
            </If>
            <Button onClick={downloadReport}>Download Report</Button>
          </Otherwise>
        </Choose>
      </div>
    </div>
  )
}

export default MoveStage
